import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CotinSpin } from '@cotin/biblioteca-componentes-react';
import AppLayoutRoute from '@/routes/AppLayoutRoute';
import IndexRedirect from '@/routes/IndexRedirect';
import AuditoriaEnabledGuard from '@/routes/AuditoriaEnabledGuard';

const LoginPage = lazy(() => import('@/modules/auth/views/LoginPage'));
const HomePage = lazy(() => import('@/modules/home/views/HomePage'));
const FuncionariosPage = lazy(() => import('@/modules/funcionarios/views/FuncionariosPage'));
const CadastrarPage = lazy(() => import('@/modules/funcionarios/views/CadastrarPage'));
const CadastrarWizardPage = lazy(() => import('@/modules/funcionarios/views/CadastrarWizardPage'));
const EditarPage = lazy(() => import('@/modules/funcionarios/views/EditarPage'));
const VisualizarPage = lazy(() => import('@/modules/funcionarios/views/VisualizarPage'));
const RecaptchaDemoPage = lazy(() => import('@/modules/recaptcha-demo/views/RecaptchaDemoPage'));
const GraficosPage = lazy(() => import('@/modules/graficos/views/GraficosPage'));
const EditoresTextoPage = lazy(() => import('@/modules/editores-texto/views/EditoresTextoPage'));
const AgendarManutencaoPage = lazy(() => import('@/modules/maintenance/views/AgendarManutencaoPage'));
const ManutencaoPage = lazy(() => import('@/modules/maintenance/views/ManutencaoPage'));
const MensagensPage = lazy(() => import('@/modules/mensagens/views/MensagensPage'));
const VisualizarMensagemPage = lazy(() => import('@/modules/mensagens/views/VisualizarMensagemPage'));
const AssistenteIAPage = lazy(() => import('@/modules/assistente-ia/views/AssistenteIAPage'));
const ConversaoPage = lazy(() => import('@/modules/conversao/views/ConversaoPage'));
const ManuaisPage = lazy(() => import('@/modules/manuais/views/ManuaisPage'));
const AuditoriaPage = lazy(() => import('@/modules/auditoria/views/AuditoriaPage'));
const LoggingPage = lazy(() => import('@/modules/logging/views/LoggingPage'));
const ServiceSchedulerPage = lazy(() => import('@/modules/service-scheduler/views/ServiceSchedulerPage'));
const ComecandoPage = lazy(() => import('@/modules/documentation/views/ComecandoPage'));
const InstalandoPage = lazy(() => import('@/modules/documentation/views/InstalandoPage'));
const FigmaPage = lazy(() => import('@/modules/documentation/views/FigmaPage'));
const AjudaPage = lazy(() => import('@/modules/documentation/views/AjudaPage'));
const ApiDocumentacaoPage = lazy(() => import('@/modules/documentation/views/ApiDocumentacaoPage'));
const AboutPage = lazy(() => import('@/modules/documentation/views/AboutPage'));
const CircuitBreakerErrorPage = lazy(() => import('@/modules/documentation/views/CircuitBreakerErrorPage'));

const UsuarioPage = lazy(() => import('@/modules/usuario/views/UsuarioPage'));

function RouteFallback() {
    return (
        <div className="d-flex align-items-center justify-content-center p-4" role="status" aria-live="polite">
            <CotinSpin id="route-fallback-spin" variation="simple" size="medium" text="Carregando..." />
        </div>
    );
}

function withSuspense(element: ReactNode) {
    return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export default function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/auth/login" element={withSuspense(<LoginPage />)} />
            <Route path="/circuit-breaker-error" element={withSuspense(<CircuitBreakerErrorPage />)} />
            <Route path="/about" element={withSuspense(<AboutPage />)} />
            <Route path="/manutencao" element={withSuspense(<ManutencaoPage />)} />
            <Route path="/maintenance" element={withSuspense(<ManutencaoPage />)} />

            <Route element={<AppLayoutRoute />}>
                <Route path="/usuario" element={withSuspense(<UsuarioPage />)} />
                <Route path="/home" element={withSuspense(<HomePage />)} />
                <Route path="/comecando" element={withSuspense(<ComecandoPage />)} />
                <Route path="/instalando" element={withSuspense(<InstalandoPage />)} />
                <Route path="/figma" element={withSuspense(<FigmaPage />)} />
                <Route path="/ajuda" element={withSuspense(<AjudaPage />)} />
                <Route path="/api-documentacao" element={withSuspense(<ApiDocumentacaoPage />)} />
                <Route path="/api" element={<Navigate to="/api-documentacao" replace />} />

                <Route path="/funcionarios" element={withSuspense(<FuncionariosPage />)} />
                <Route path="/funcionarios/cadastrar" element={withSuspense(<CadastrarPage />)} />
                <Route path="/funcionarios/cadastrar-wizard" element={withSuspense(<CadastrarWizardPage />)} />
                <Route path="/funcionarios/editar/:id" element={withSuspense(<EditarPage />)} />
                <Route path="/funcionarios/visualizar/:id" element={withSuspense(<VisualizarPage />)} />

                <Route path="/recaptcha-demo" element={withSuspense(<RecaptchaDemoPage />)} />
                <Route path="/graficos" element={withSuspense(<GraficosPage />)} />
                <Route path="/editores-texto" element={withSuspense(<EditoresTextoPage />)} />
                <Route path="/agendar-manutencao" element={withSuspense(<AgendarManutencaoPage />)} />

                <Route path="/mensagens" element={withSuspense(<MensagensPage />)} />
                <Route path="/mensagens/visualizar/:id" element={withSuspense(<VisualizarMensagemPage />)} />

                <Route path="/assistente-ia" element={withSuspense(<AssistenteIAPage />)} />
                <Route path="/conversao" element={withSuspense(<ConversaoPage />)} />
                <Route path="/manuais" element={withSuspense(<ManuaisPage />)} />
                <Route path="/logging" element={withSuspense(<LoggingPage />)} />
                <Route path="/agendamento-servico" element={withSuspense(<ServiceSchedulerPage />)} />

                <Route
                    path="/auditoria"
                    element={<AuditoriaEnabledGuard>{withSuspense(<AuditoriaPage />)}</AuditoriaEnabledGuard>}
                />
            </Route>

            <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
    );
}